import { storeToRefs } from 'pinia'
import { nextTick, watch } from 'vue'
import { createFeatures } from '@/services/map/FeatureService'
import { useMapStore } from '@/stores/map'

export function useMapSync({
  featureSource,
  layers,
  triggerUpdate,
  fitToLayer,
  changeBaseLayer,
  zoomToFeatures = true,
}) {
  const mapStore = useMapStore()
  const { features, activeBaseLayer, activeInfoLayers } = storeToRefs(mapStore)

  // Watch for features changes
  watch(features, newFeatures => {
    if (!featureSource.value) {
      return
    }
    featureSource.value.clear()

    if (newFeatures.length) {
      const fset = createFeatures({
        type: 'FeatureCollection',
        features: newFeatures,
      })
      featureSource.value.addFeatures(fset)

      nextTick(() => {
        if (zoomToFeatures && featureSource.value) {
          fitToLayer(featureSource.value)
        } else {
          triggerUpdate()
        }
      })
    }
  })

  // Watch for active base layer changes
  watch(activeBaseLayer, newBaseLayer => {
    if (newBaseLayer) {
      changeBaseLayer(newBaseLayer)
    }
  })

  // Watch for active info layers changes
  watch(activeInfoLayers, newLayers => {
    if (!layers.value.info) return

    layers.value.info
      .getLayers()
      .getArray()
      .forEach(layer => {
        layer.setVisible(newLayers.includes(layer.get('id')))
      })
    triggerUpdate()
  })

  // Function to update layers in the store
  const updateStoreLayers = (baseLayers, infoLayers, overlayLayers) => {
    mapStore.layers = {
      ...mapStore.layers,
      base: baseLayers,
      info: infoLayers,
      overlays: overlayLayers,
    }
  }

  // Add features and optionally zoom to them
  const addFeatures = newFeatures => {
    if (!featureSource.value) return

    const fset = createFeatures({
      type: 'FeatureCollection',
      features: newFeatures,
    })
    featureSource.value.addFeatures(fset)

    nextTick(() => {
      if (zoomToFeatures && featureSource.value) {
        fitToLayer(featureSource.value)
      } else {
        triggerUpdate()
      }
    })
  }

  return {
    mapStore,
    updateStoreLayers,
    addFeatures,
  }
}
